/*
 * EdgeUnreachableNotice — placeholder shown in a node's views when the
 * selected node is unreachable. Replaces panels that would otherwise pile up
 * stuck fetches and freeze the rest of the UI. Includes a quick-jump button
 * back to the Fleet Overview so the user can pick a healthy node without
 * having to fight with the sidebar dropdown.
 *
 * A phone is unreachable far more often than an edge, and for ordinary
 * reasons: it answers only while its tunnel is up. So it gets its own words,
 * and a phone that is heartbeating but holds no channel is running an app from
 * before phones answered the console, which only an app update fixes.
 */
import React from "react";
import { FiAlertTriangle, FiLayers } from "react-icons/fi";

function explain(phone, appOutdated) {
  if (appOutdated) {
    return {
      title: "Phone app needs an update",
      lead: "is connected, but its app is older than this console's view of phones.",
      detail:
        "Update the Weavian app on the phone and it will appear here with its links and traffic. " +
        "Its registration and tunnel are unaffected.",
    };
  }
  if (phone) {
    return {
      title: "Phone not connected",
      lead: "is not connected right now. A phone answers only while its tunnel is up.",
      detail:
        "This page reconnects on its own when the phone does. What the phone carried " +
        "while it was connected is under Pilot Insights, which works while it is away.",
    };
  }
  return {
    title: "Edge unavailable",
    lead:
      "is not responding on its API endpoint right now. Live edge data, traffic shaping " +
      "and diagnostics need a reachable proxy through the cloud anchor.",
    detail:
      "The dashboard is still polling /healthz in the background and will reconnect " +
      "automatically once the edge proxy responds. In the meantime you can switch to another " +
      "node from the Fleet Overview, or use server-side telemetry views (Pilot Insights, Deep " +
      "Telemetry, Activity Log) which work even when the edge is offline.",
  };
}

export default function EdgeUnreachableNotice({ nodeId, onGoToFleet, feature, phone, appOutdated }) {
  const words = explain(phone, appOutdated);
  // A phone that is away is ordinary, so it is not drawn as an alarm; an app
  // that needs updating is something to act on, and an edge that stopped
  // answering is a fault.
  const tone = appOutdated ? "#f59e0b" : phone ? "#94a3b8" : "#ef4444";
  return (
    <div className="app-content">
      <section className="hero">
        <div>
          <h1>{feature || words.title}</h1>
          <p>
            <strong>{nodeId || "The selected node"}</strong> {words.lead}
          </p>
        </div>
      </section>
      <div className="panel" style={{ borderLeft: `4px solid ${tone}` }}>
        <div className="panel-title" style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <FiAlertTriangle size={18} color={tone} />
          {phone ? words.title : "Node unreachable"}
        </div>
        <div className="panel-sub">{words.detail}</div>
        <div style={{ marginTop: 16, display: "flex", gap: 12, flexWrap: "wrap" }}>
          {onGoToFleet && (
            <button
              type="button"
              className="btn btn-primary"
              onClick={onGoToFleet}
              style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
            >
              <FiLayers size={14} /> Go to Fleet Overview
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
